import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Mail, MapPin, Linkedin, Phone } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const navigate = useNavigate();

  const handleReservation = (e) => {
    e.preventDefault();
    
    // Si on est sur la page contact, scroll vers le formulaire
    if (location.pathname === '/contact') {
      const formulaire = document.getElementById('formulaire-contact');
      if (formulaire) {
        formulaire.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      // Sinon, naviguer vers la page contact
      navigate('/contact');
    }
  };

  return (
    <footer className="relative mt-20" data-testid="footer">
      {/* Section principale */}
      <div className="glass p-12 mx-4 mb-8 rounded-3xl">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            {/* ATOM Group SA */}
            <div data-testid="footer-brand">
              <div className="flex items-center mb-4">
                <img 
                  src="/assets/logo/atom-logo-white.png" 
                  alt="ATOM Group SA" 
                  className="h-8 w-auto"
                  data-testid="footer-logo"
                />
              </div>
              <p className="text-gray-300 mb-6">
                Des décisions financières claires, simplement. Conseil indépendant et transparent pour votre avenir financier.
              </p>

              {/* Membre ACA */}
              <a
                href="https://aca-courtiers.ch/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mb-6 opacity-60 hover:opacity-100 transition-opacity"
                data-testid="footer-aca-link"
                title="ATOM Group SA est membre de l'Association des Courtiers en Assurances"
              >
                <span className="text-gray-400 text-[11px] uppercase tracking-wider">
                  Membre de
                </span>
                <img
                  src="/assets/certifications/aca-logo-white.png"
                  alt="Association des Courtiers en Assurances (ACA)"
                  className="h-5 w-auto"
                  loading="lazy"
                />
              </a>

              <div className="flex space-x-4">
                <a href="mailto:contact@atom-group.ch" className="p-2 glass rounded-xl hover:bg-blue-500/20 transition-colors" data-testid="footer-email-link">
                  <Mail size={20} />
                </a>
                <a href="https://www.linkedin.com/company/atom-group-sa/about/" target="_blank" rel="noopener noreferrer" className="p-2 glass rounded-xl hover:bg-blue-500/20 transition-colors" data-testid="footer-linkedin-link">
                  <Linkedin size={20} />
                </a>
              </div>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-4">Contact</h4>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <MapPin size={16} className="text-blue-300 mt-1" />
                  <div className="text-gray-300 text-sm">
                    <div className="font-semibold">ATOM Group SA</div>
                    <div>c/o Microcity SA</div>
                    <div>Rue de la Pierre-à-Mazel 39</div>
                    <div>2000 Neuchâtel, Suisse</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone size={16} className="text-blue-300" />
                  <a href="tel:+41325391627" className="text-gray-300 text-sm hover:text-white transition-colors">
                    +41 32 539 16 27
                  </a>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail size={16} className="text-blue-300" />
                  <a href="mailto:contact@atom-group.ch" className="text-gray-300 text-sm hover:text-white transition-colors">
                    contact@atom-group.ch
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          {/* Bouton aligné à gauche sous ATOM Group SA */}
          <div className="mb-8">
            <button 
              onClick={handleReservation} 
              className="btn-glass btn-primary px-8 py-3" 
              data-testid="footer-cta-contact"
            >
              Réserver un rendez-vous
            </button>
          </div>
        </div>
      </div>

      {/* Footer bottom */}
      <div className="border-t border-gray-800 py-6" data-testid="footer-bottom">
        <div className="container">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © {currentYear} ATOM Group SA. Tous droits réservés.
            </p>
            
            {/* Liens légaux */}
            <div className="flex space-x-6" data-testid="footer-legal-links">
              <Link to="/faq" className="text-gray-400 text-sm hover:text-white transition-colors" data-testid="footer-link-faq">
                FAQ
              </Link>
              <Link to="/mentions-legales" className="text-gray-400 text-sm hover:text-white transition-colors" data-testid="footer-link-mentions">
                Mentions légales
              </Link>
              <Link to="/confidentialite" className="text-gray-400 text-sm hover:text-white transition-colors" data-testid="footer-link-confidentialite">
                Confidentialité
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;